// NPM Imports
import React, { Component } from "react";
import { connect } from "react-redux";
import {
  Modifier,
  RichUtils,
  EditorState,
  getDefaultKeyBinding,
} from "draft-js";
import Editor from "draft-js-plugins-editor";
import createToolbarPlugin from "draft-js-static-toolbar-plugin";

// Components Imports
import customBlockRenderer from "./BlockStyling";
import HTMLtoDraft from "../../utils/HTMLtoDraft/HTMLtoDraft";
import getEditorHTML from "./getEditorHtml";
import getEditorDiff from "./getEditorDiff";
import getCompositeDecorator from "./decorator";

// Actions Imports
import EditorActions from "../../actions/EditorActions";

// Internal Imports
import { DOCUMENTS } from "../../constants";
import "draft-js-static-toolbar-plugin/lib/plugin.css";
import {
  checkSelectionHasEntity,
  previousCharEntity,
  removeEntityAndReplaceText,
  updateFormFields,
} from "../../utils/draftUtils";
import addLinkPlugin from "../RevvToolBar/plugins/addLinkPlugin";

// Destructuring
const {
  COMPONENT_FEATURE,
  DOCUMENT_MODE,
  FONT_SIZE_OPTIONS,
  FONT_COLOR_OPTIONS,
  FONT_FAMILY_OPTIONS,
  FONT_WEIGHT_OPTIONS,
} = DOCUMENTS;
const {
  nukeActiveEditor,
  updateEditorState,
  initEditorState,
  updateEditorHighlightSelection,
} = EditorActions;
const staticToolbarPlugin = createToolbarPlugin();
const plugins = [staticToolbarPlugin, addLinkPlugin];

const customKeyBinding = event => {
  const keyBinding = getDefaultKeyBinding(event);
  if (keyBinding === "undo") {
    return "handleUndo";
  }
  if (keyBinding === "redo") {
    return "handleRedo";
  }
  return keyBinding;
};

const FONT_STYLE_OPTIONS = {
  ...FONT_SIZE_OPTIONS,
  ...FONT_COLOR_OPTIONS,
  ...FONT_FAMILY_OPTIONS,
  ...FONT_WEIGHT_OPTIONS,
};

class EditEditor extends Component {
  state = {
    editorState: EditorState.createEmpty(),
    undoStack: [],
    redoStack: [],
  };

  blockRendererFn = customBlockRenderer(this._getEditorState, this._onChange);

  compositeDecorator = getCompositeDecorator();

  componentDidMount() {
    const { mode, section } = this.props;
    const { data, currentContent, undoStack, redoStack } = section;
    let { editorState } = EditorState.createEmpty(this.compositeDecorator);
    if (COMPONENT_FEATURE[mode].allowDiff) {
      editorState =
        Object.keys(currentContent).length !== 0
          ? EditorState.createWithContent(
              currentContent,
              this.compositeDecorator,
            )
          : editorState;
    } else {
      editorState =
        Object.keys(data).length !== 0
          ? EditorState.createWithContent(data, this.compositeDecorator)
          : editorState;
    }
    this.setState({
      editorState,
      undoStack: undoStack || [],
      redoStack: redoStack || [],
    });

    // const {
    //   activeEditorState,
    //   activeComponent,
    //   isCustomTableTextEditor,
    //   isComponentActive,
    // } = this.props;

    // // Initiate the Editor state
    // if (
    //   isCustomTableTextEditor &&
    //   isComponentActive &&
    //   !activeEditorState &&
    //   !activeComponent
    // ) {
    //   console.debug("-----table-wala-case-----");
    //   this.props.initEditorState(this.state.editorState, section);
    // }

    this._handleClickEventListner(true);
  }

  componentDidUpdate() {
    const { section, isComponentActive, isHoverdComponentActive } = this.props;
    if (
      (isComponentActive && isHoverdComponentActive) ||
      !this.props.isDocCustomTextEditor
    ) {
      this.focus();
    }
    const {
      activeEditorState,
      activeComponent,
      isDocCustomTextEditor,
    } = this.props;

    // Initiate the Editor state for Normal Custom Text
    if (
      isDocCustomTextEditor &&
      isHoverdComponentActive &&
      isComponentActive &&
      !activeEditorState &&
      !activeComponent
    ) {
      console.debug("-----EditEditor init------");
      this.props.initEditorState(this.state.editorState, section);
    }
  }

  componentWillUnmount() {
    this._handleClickEventListner(false);
  }

  /** ::: "Prevent_The_Click_Event_On_Active_Editor" :::
   * it will prevent the active editor to deactivate,
   * in a case of adding a new text component and then click on editor
   */
  _handleClickEventListner = isAddEvent => {
    const { isComponentActive, isDocCustomTextEditor } = this.props;
    if (isDocCustomTextEditor && isComponentActive) {
      const { section } = this.props;
      const id = `edit_editor_id_${section ? section.id : null}`;
      const currentEditorElement = document.getElementById(id);
      if (currentEditorElement) {
        if (isAddEvent) {
          currentEditorElement.addEventListener(
            "click",
            this._preventClickInsideEditor,
            false,
          );
        } else {
          currentEditorElement.removeEventListener(
            "click",
            this._preventClickInsideEditor,
            false,
          );
        }
      }
    }
  };

  _preventClickInsideEditor = event => {
    event.preventDefault();
    event.stopPropagation();
  };

  /** ::: "Handle_Editor_On_Change" :::
   * If there is an acitve editor and active component then, update the Redux Wala "editorState",
   * else update the local wala "editorState
   */
  _onChange = editorState => {
    const { activeComponent, section, isComponentActive } = this.props;
    if (
      isComponentActive &&
      activeComponent &&
      activeComponent.id === section.id
    ) {
      this.props.updateEditorState(editorState);
    } else {
      this.setState({ editorState });
    }
  };

  /** ::: "Get_The_Correct_Editor_State" :::
   * If there is an acitve editor then, get the Redux Wala "editorState",
   * else get the local wala "editorState
   */
  _getEditorState = () => {
    const { activeComponent, activeEditorState, section } = this.props;
    if (activeComponent && activeComponent.id === section.id) {
      return activeEditorState;
    }
    return this.state.editorState;
  };

  focus = () => {
    if (this.editEditor) {
      this.editEditor.focus();
    }
  };

  _handleChange = newEditorState => {
    const editorState = this._getEditorState();
    if (
      editorState &&
      checkSelectionHasEntity(editorState) &&
      editorState.getCurrentContent() !== newEditorState.getCurrentContent()
    ) {
      this._onChange(editorState);
    } else if (newEditorState) {
      this._onChange(newEditorState);
    }
  };

  _handleRemoveRange = () => {
    const editorState = this._getEditorState();
    const contentState = editorState.getCurrentContent();
    const selectionState = editorState.getSelection();
    const direction = selectionState.isBackward ? "backward" : "forward";
    const anchorOffset = selectionState.getAnchorOffset();
    const focusOffset = selectionState.getFocusOffset();
    if (anchorOffset === focusOffset) {
      return false;
    }
    const textWithoutRange = Modifier.removeRange(
      contentState,
      selectionState,
      direction,
    );
    this._onChange(
      EditorState.push(editorState, textWithoutRange, "remove-range"),
    );
    return true;
  };

  /**
   * @param {boolean} isMouseHover - Flag used to prevent unwanted api trigger during doc diff accept/reject flow
   */
  _handleBlur = isMouseHover => {
    const { redoStack, undoStack } = this.state;
    const { section, handleBlur, mode, highlightedContent } = this.props;
    const editorState = this._getEditorState();

    // check if there is any change in the section or not
    let change = !(
      (COMPONENT_FEATURE[mode].allowDiff &&
        section.currentContent === editorState.getCurrentContent()) ||
      (!COMPONENT_FEATURE[mode].allowDiff &&
        section.data === editorState.getCurrentContent())
    );

    let newSection = {
      ...section,
    };
    // if undo is allowed, then save undo and redo stack
    if (COMPONENT_FEATURE[mode].allowUndo) {
      newSection = {
        ...newSection,
        redoStack,
        undoStack,
      };
    }

    // if change is there, update diffState, current content, data and html
    if (change) {
      if (COMPONENT_FEATURE[mode].allowDiff) {
        const diffState = getEditorDiff(editorState, section.data);
        newSection = {
          ...newSection,
          diffState: diffState.getCurrentContent(),
          currentContent: editorState.getCurrentContent(),
          html: getEditorHTML(editorState.getCurrentContent(), mode),
          diffHtml: getEditorHTML(diffState.getCurrentContent(), mode),
        };
      } else {
        newSection = {
          ...newSection,
          data: editorState.getCurrentContent(),
          html: getEditorHTML(editorState.getCurrentContent(), mode),
          form_field: updateFormFields(editorState, section.form_field),
        };
      }
      // get header of the current content
      const firstBlock = editorState.getCurrentContent().getFirstBlock();
      newSection.header = firstBlock.getText().trim();
      if (firstBlock.getType().startsWith("header")) {
        newSection.label = firstBlock.getText().trim();
      } else {
        newSection.label = newSection.header;
      }
    }

    // check if there is any highlight
    if (highlightedContent) {
      change = change || null;
      if (COMPONENT_FEATURE[mode].allowDiff) {
        const diffState = getEditorDiff(highlightedContent, section.data);
        newSection = {
          ...newSection,
          diffState: diffState.getCurrentContent(),
          diffHtml: getEditorHTML(diffState.getCurrentContent(), mode),
          highlightedContent,
          highlightedHtml: getEditorHTML(highlightedContent, mode),
        };
      } else {
        newSection = {
          ...newSection,
          highlightedContent,
          highlightedHtml: getEditorHTML(highlightedContent, mode),
        };
      }
    }
    handleBlur(newSection, change, isMouseHover, section);

    // Update the local "EditorState" to redux wala "EditorState" to reflect the latest changes
    this.setState({ editorState });

    // Nuke the Redux wala EditorState
    const { activeComponent } = this.props;
    if (activeComponent) {
      this.props.nukeActiveEditor();
    }

    // Deactivate the current clicked component in case of "Custom Text"
    const {
      updateDocumentClickedComponentID,
      isDocCustomTextEditor,
    } = this.props;
    if (isDocCustomTextEditor && updateDocumentClickedComponentID) {
      updateDocumentClickedComponentID(null, true, null);
    }
  };

  _handleUndo = () => {
    const editorState = this._getEditorState();
    this._onChange(EditorState.undo(editorState));
  };

  _handleRedo = () => {
    const editorState = this._getEditorState();
    this._onChange(EditorState.redo(editorState));
  };

  onTab = e => {
    const editorState = this._getEditorState();
    const maxDepth = 4;
    const updatedEditorState = RichUtils.onTab(e, editorState, maxDepth);
    this._handleChange(updatedEditorState);
  };

  _removeEntity = (editorState, prevCharEntity) => {
    const currentContent = editorState.getCurrentContent();
    const {
      currentContent: newContent,
      selectionState,
    } = removeEntityAndReplaceText(
      currentContent,
      prevCharEntity.entity.data.id,
      "",
      true,
      editorState,
      prevCharEntity,
    );
    const newEditorState = EditorState.set(editorState, {
      currentContent: newContent,
    });
    const updatedEditorSelection = EditorState.acceptSelection(
      newEditorState,
      selectionState,
    );
    return updatedEditorSelection;
  };

  handleKeyCommand = command => {
    const editorState = this._getEditorState();
    let newState;
    if (command === "handleUndo") {
      newState = this._handleUndo();
      return "handled";
    }
    if (command === "handleRedo") {
      newState = this._handleRedo();
      return "handled";
    }
    if (command === "backspace") {
      const prevCharEntity = previousCharEntity(editorState);
      if (prevCharEntity) {
        this._handleChange(this._removeEntity(editorState, prevCharEntity));
        return "handled";
      }
      if (!prevCharEntity) {
        const removed = this._handleRemoveRange();
        if (!removed) {
          return "not-handled";
        }
        return "handled";
      }
      return "not-handled";
    }
    newState = RichUtils.handleKeyCommand(editorState, command);
    if (newState !== undefined && newState !== null) {
      this._handleChange(newState);
      return "handled";
    }
    return "not-handled";
  };

  _remove_copied_styles = contentState => {
    // on paste remove sytles that come along with it

    contentState.getBlocksAsArray().forEach(block => {
      if (block.data && block.data._root && block.data._root.entries) {
        block.data._root.entries[0][1] = "";
      }
    });
    return contentState;
  };

  handlePastedText = (text, textHtml) => {
    const editorState = this._getEditorState();

    // if the cursor already has a entity attached, copy-paste should not be allowed between them
    if (checkSelectionHasEntity(editorState)) return "handled";

    // if text or text html is null or undefined, stop pasting
    if (!(text && textHtml)) return "handled";

    const contentState = HTMLtoDraft(textHtml, DOCUMENT_MODE.PREVIEW);
    const pastedBlocks = this._remove_copied_styles(contentState).blockMap;
    const newContent = Modifier.replaceWithFragment(
      editorState.getCurrentContent(),
      editorState.getSelection(),
      pastedBlocks,
    );

    const newEditorState = EditorState.push(
      editorState,
      newContent,
      "insert-fragment",
    );
    this._handleChange(newEditorState);
    return "handled";
  };

  /** ::: "Handle_Editor_Click" :::
   * This method is for initiaing the editor state with current clicked section
   */
  _handleEditEditorClick = (event, id) => {
    const { isCustomTableTextEditor, isDocCustomTextEditor } = this.props;
    if (isDocCustomTextEditor || isCustomTableTextEditor) {
      const { activeEditorState, activeComponent } = this.props;
      let checkCustomTextCondition = null;
      if (!activeEditorState && !activeComponent) {
        // in case of normal custom text
        if (isDocCustomTextEditor) {
          checkCustomTextCondition = !this.props.isComponentActive;
        }
        // in case of Table custom text
        else if (isCustomTableTextEditor) {
          checkCustomTextCondition = true;
        }
      }
      const { isLocked, lock, section } = this.props;
      if (!isLocked && (checkCustomTextCondition && !lock)) {
        this.props.initEditorState(this.state.editorState, section);
      }
    }
  };

  /** ::: "Handle_Editor_On_Blur" :::
   * need to do the following actions,
   * 1. If there is an active editor then, clear the redux wala "editorState",
   * 2. If it is a case of normal custom text then, update the "clickedActiveComponentId" to "null"
   */
  _handleOnBlur = () => {
    const {
      activeComponent,
      activeEditorState,
      isCustomTableTextEditor,
      isDocCustomTextEditor,
    } = this.props;
    if (isDocCustomTextEditor && activeComponent && activeEditorState) {
      this._handleBlur();
    } else if (isCustomTableTextEditor) {
      this._handleBlur();
    }
  };

  _getEditorClassName = () => {
    let className = "editor ";
    if (this.props.isComponentActive) {
      className += " active-editor";
    }
    return className;
  };

  render() {
    const { section } = this.props;
    const editorState = this._getEditorState();
    return (
      <div
        id={`edit_editor_id_${section.id}`}
        className={this._getEditorClassName()}
        onClick={e => this._handleEditEditorClick(e, section.id)}
        onKeyPress={() => {}}
        onBlur={this._handleOnBlur}
        role="button"
        tabIndex="0">
        {editorState && (
          <Editor
            customStyleMap={FONT_STYLE_OPTIONS}
            editorState={editorState}
            placeholder="Start typing..."
            onChange={es => this._handleChange(es)}
            handleKeyCommand={this.handleKeyCommand}
            keyBindingFn={customKeyBinding}
            onTab={this.onTab}
            plugins={plugins}
            ref={element => {
              this.editEditor = element;
            }}
            blockRendererFn={this.blockRendererFn}
            handlePastedText={this.handlePastedText}
          />
        )}
      </div>
    );
  }
}

const mapStateToProps = state => {
  const {
    activeEditorState,
    highlightedContent,
    activeComponent,
  } = state.EditorStore;
  return {
    activeComponent,
    activeEditorState,
    highlightedContent,
  };
};

const mapDispatchToProps = {
  nukeActiveEditor,
  updateEditorState,
  initEditorState,
  updateEditorHighlightSelection,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(EditEditor);
